// ═══════════════════════════════════════════════════════════════
// useBusinessProfile — single subscription to the signed-in mechanic's
// business profile (name/phone/email/address/logoUrl). Used by Settings
// (form state) and by Jobs (snapshot fields when creating shared
// estimates/invoices).
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect } from 'react';
import { onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { businessProfileDoc } from '../database.js';
import { resolveLaborPresets } from './labor-presets.js';
import { resolveTaxPresets, normalizeTaxPresets } from './tax-presets.js';

const EMPTY_PROFILE = {
  name: '',
  phone: '',
  email: '',
  address: '',
  logoUrl: '',
  logoPath: '',
  laborPresets: [],
  removedDefaultLaborPresetIds: [],
  partSearchLinks: [],
  taxPresets: [],
};

export function useBusinessProfile() {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      businessProfileDoc(),
      (snap) => {
        if (snap.exists()) {
          // Estimate pending serverTimestamps for instant UI updates.
          const data = snap.data({ serverTimestamps: 'estimate' });
          setProfile({
            ...EMPTY_PROFILE,
            ...data,
            laborPresets: resolveLaborPresets(
              data.laborPresets,
              data.removedDefaultLaborPresetIds,
            ),
            removedDefaultLaborPresetIds: Array.isArray(data.removedDefaultLaborPresetIds)
              ? data.removedDefaultLaborPresetIds
              : [],
            taxPresets: resolveTaxPresets(data.taxPresets),
          });
        } else {
          setProfile({
            ...EMPTY_PROFILE,
            laborPresets: resolveLaborPresets([]),
          });
        }
        setReady(true);
      },
      (err) => {
        console.error('Business profile load failed:', err);
        setReady(true);
      }
    );
    return unsub;
  }, []);

  return { profile, ready };
}

// Save a tax rate as a preset from wherever the mechanic happens to
// be — the job editor on the customer page, or the calendar sidebar.
//
// Reads before writing rather than merging blind: two devices editing
// the same profile is rare but a blind array write would drop the
// other one's presets entirely. Re-saving under the same name updates
// that preset instead of adding a duplicate, because the name is how
// the mechanic identifies it and two "Lancaster County" rows with
// different rates is a bug they'd have to go and clean up.
export async function saveTaxPreset(preset) {
  const ref = businessProfileDoc();
  const snap = await getDoc(ref);
  const existing = normalizeTaxPresets(snap.exists() ? snap.data().taxPresets : []);
  const incoming = normalizeTaxPresets([{
    ...preset,
    id: preset?.id || `tax-${Date.now().toString(36)}`,
  }])[0];
  if (!incoming) return null;

  const key = incoming.label.toLowerCase();
  const idx = existing.findIndex((p) => p.label.toLowerCase() === key);
  const next = idx >= 0
    ? existing.map((p, i) => (i === idx ? { ...incoming, id: p.id || incoming.id } : p))
    : [...existing, incoming];

  await setDoc(ref, { taxPresets: next }, { merge: true });
  return incoming;
}

// Snapshot the fields we copy into a share/invoice doc at Send time.
// Caller should fall back to safe defaults if the mechanic hasn't filled
// in their profile yet — public pages should still render.
export function businessSnapshot(profile) {
  return {
    businessName:    profile?.name    || '',
    businessPhone:   profile?.phone   || '',
    businessEmail:   profile?.email   || '',
    businessAddress: profile?.address || '',
    businessLogoUrl: profile?.logoUrl || '',
  };
}
